import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type { LinkBlock } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { LinkIcon } from './contentBlockIcons';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getTitleTypography } from './styles/titleTypography';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { InteractionMessage } from './InteractionMessage';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  isSafeExternalUrl,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { RichTextContent } from './RichTextContent';

const descriptionResetStyles = createRichTextContentResetStyles(
  'link-description-rich',
  '0.6em'
);

export interface LinkRendererProps {
  block: LinkBlock;
  onInteractionComplete?: () => void;
  onLinkOpened?: () => void;
  /**
   * NA-319-style relaunch/session-nav restore, same mechanism as Policy
   * Acknowledgement/Electronic Signature (see CourseCanvas.tsx): Link has no
   * persisted per-learner response either, so a prior click is only known
   * from either the learner's real, actually-clicked state earlier in this
   * browser session, or — when the lesson is already confirmed complete and
   * this block instance requires interaction — a guaranteed-clicked
   * fallback, since the lesson could not have completed without the click.
   */
  initialLinkOpened?: boolean;
}

export function LinkRenderer({
  block,
  onInteractionComplete,
  onLinkOpened,
  initialLinkOpened,
}: LinkRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const richTextExtensions = useMemo(
    () => createRichTextRendererExtensions(),
    []
  );

  const titleLevel = block.titleLevel ?? 'h2';
  const TitleTag = titleLevel;
  const titleTypography = getTitleTypography(tokens, titleLevel, { isMobile });
  const linkTitleStyle: React.CSSProperties = {
    ...styles.linkTitle,
    fontSize: titleTypography.size,
    fontWeight: titleTypography.weight,
    lineHeight: titleTypography.lineHeight,
  };

  const [isLinkOpened, setIsLinkOpened] = useState(
    () => initialLinkOpened === true
  );
  const [isAnchorHovered, setIsAnchorHovered] = useState(false);

  // Mirrors PolicyAcknowledgementRenderer's restore guard: `initialLinkOpened`
  // can resolve asynchronously after first mount, and the `!isLinkOpened`
  // check avoids clobbering an in-progress or already-locked state.
  useEffect(() => {
    if (initialLinkOpened && !isLinkOpened) {
      setIsLinkOpened(true);
    }
  }, [initialLinkOpened, isLinkOpened]);

  const requireInteraction = block.requireInteraction ?? false;

  const isRichDescription = Boolean(block.description) && typeof block.description !== 'string';
  const descriptionHtml = useMemo(() => {
    if (!block.description || typeof block.description === 'string') {
      return '';
    }
    return safeGenerateRichTextHTML(
      block.description as JSONContent,
      richTextExtensions
    );
  }, [block.description, richTextExtensions]);

  const displayLinkText = block.linkText?.trim() || block.linkUrl?.trim() || '';

  // Trust-based on click: the interaction completes the moment the learner
  // clicks, regardless of whether the URL was safe to open — there is no
  // return-to-tab detection for this block, so withholding completion here
  // would strand a learner on a block whose author left (or mistyped) an
  // empty/invalid URL, with no way to recover short of a republish.
  const handleOpenLink = useCallback(() => {
    if (block.linkUrl && isSafeExternalUrl(block.linkUrl)) {
      window.open(block.linkUrl, '_blank', 'noopener,noreferrer,width=800,height=600');
    }
    setIsLinkOpened(true);
    onLinkOpened?.();
    onInteractionComplete?.();
  }, [block.linkUrl, onLinkOpened, onInteractionComplete]);

  const anchorStyle = useMemo<React.CSSProperties>(
    () =>
      isAnchorHovered
        ? { ...styles.linkAnchor, ...styles.linkAnchorHover }
        : styles.linkAnchor,
    [isAnchorHovered, styles]
  );

  return (
    <div style={styles.linkContainer}>
      {block.title ? <TitleTag style={linkTitleStyle}>{block.title}</TitleTag> : null}

      {block.description ? (
        isRichDescription ? (
          <>
            <style>{descriptionResetStyles}</style>
            <RichTextContent
              className="link-description-rich"
              style={styles.linkDescription}
              html={descriptionHtml}
            />
          </>
        ) : (
          <p style={styles.linkDescription}>{block.description as string}</p>
        )
      ) : null}

      {displayLinkText && (
        <div style={styles.linkRow}>
          <button
            type="button"
            style={anchorStyle}
            onClick={handleOpenLink}
            onMouseEnter={() => setIsAnchorHovered(true)}
            onMouseLeave={() => setIsAnchorHovered(false)}
          >
            {displayLinkText}
            <LinkIcon style={styles.linkAnchorIcon} />
          </button>
        </div>
      )}

      {requireInteraction && !isLinkOpened && (
        <InteractionMessage
          variant="boxed"
          topOffset={50}
          requireInteraction={requireInteraction}
          isComplete={false}
          message={block.interactionMessage}
          fallbackMessage={t('link.review_to_continue')}
        />
      )}
    </div>
  );
}

LinkRenderer.displayName = 'LinkRenderer';
